<template>
  <div class="partners-container" :class="{ 'is-embed': embed }">
    <div v-if="!embed" class="title">合作伙伴</div>

    <div class="tabs">
      <span
        class="tab"
        :class="{ active: activeTab === 'customers' }"
        @click="switchTab('customers')"
      >典型客户</span>
      <span
        class="tab"
        :class="{ active: activeTab === 'partners' }"
        @click="switchTab('partners')"
      >合作伙伴</span>
    </div>

    <div class="content">
      <template v-if="currentList.length > 0">
        <div class="partners-grid">
          <div
            v-for="(img, index) in currentList"
            :key="`${activeTab}-${index}`"
            class="partners-item"
          >
            <img
              :src="img"
              :alt="`${activeTab === 'customers' ? '客户' : '合作伙伴'} logo`"
              class="partner-image"
              loading="lazy"
            >
          </div>
        </div>

      </template>
      <div v-else class="empty-tip">
        {{ activeTab === 'customers' ? '暂无典型客户' : '暂无合作伙伴' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  embed: { type: Boolean, default: false }
});

const activeTab = ref('customers');

// 使用 import.meta.glob 动态加载图片（支持 png/jpg/jpeg/svg/webp）
const customersModules = import.meta.glob('../assets/partners/customers/*.{png,jpg,jpeg,gif,svg,webp}', { eager: true, import: 'default' });
const partnersModules = import.meta.glob('../assets/partners/partners/*.{png,jpg,jpeg,gif,svg,webp}', { eager: true, import: 'default' });

/** 按路径解析 glob 结果为 URL */
function urlFromGlob(modules, basename) {
  const key = Object.keys(modules).find((p) => p.endsWith(`/${basename}`) || p.endsWith(basename));
  return key ? modules[key] : null;
}

/**
 * 典型客户固定顺序（按标注 1–30）
 * 国家网信办、工信部、公安部、广电总局、国家铁路局、新华社、中核、中国船舶、中国商飞、中国电科、
 * 国家能源、国家电投、中国华能、中国烟草、国家电网、中国电信、中国联通、中国移动、CEC 中国电子、国铁集团、
 * 中国信科、中国铁建、中国中铁、中国通号、铁科院、中科院计算所、北京电视台、歌华有线、北京地铁、厦门地铁
 */
const CUSTOMER_ORDER = [
  'cac.png',
  'miit.png',
  'mps.png',
  'nrta.png',
  'nra.png',
  'xinhua.png',
  'cnnc.png',
  'cssc.png',
  'comac.png',
  'cetc.png',
  'chnenergy.svg',
  'spic.png',
  'huaneng.png',
  'tobacco.png',
  'sgcc.png',
  'telecom.png',
  'unicom.png',
  'mobile.svg',
  'cec.png',
  'china-railway.png',
  'cict.png',
  'crcc.png',
  'crec.png',
  'crsc.png',
  'rails.png',
  'ict-cas.png',
  'btv.png',
  'gehua.gif',
  'bj-subway.png',
  'xm-metro.png',
];

const customersList = computed(() =>
  CUSTOMER_ORDER.map((b) => urlFromGlob(customersModules, b)).filter(Boolean)
);

/**
 * 合作伙伴固定顺序
 * 公安部一所、公安部三所、中国电信、中国移动、中国联通、浪潮、太极、观安、优炫、中兴、360
 */
const PARTNER_ORDER = [
  'mps-institute-1.png',
  'mps-institute-3.png',
  'china-telecom.png',
  'china-mobile.svg',
  'china-unicom.png',
  'inspur.png',
  'taiji.png',
  '1_45.gif',
  '2_27.gif',
  'zte.png',
  'qihoo-360.png',
];

const partnersList = computed(() =>
  PARTNER_ORDER.map((b) => urlFromGlob(partnersModules, b)).filter(Boolean)
);

const currentList = computed(() =>
  activeTab.value === 'customers' ? customersList.value : partnersList.value
);

function switchTab(tab) {
  activeTab.value = tab;
}
</script>

<style scoped>
.partners-container {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding: 4rem 0;
    overflow: visible;
    background: linear-gradient(180deg, #fefefe 0%, #fafafa 100%);
    text-align: center;
  }

.partners-container::before {
    content: '';
    position: absolute;
    inset: 0;
    background: transparent;
    pointer-events: none;
  }

.partners-container.is-embed {
  padding: 0 0 2rem;
  background: transparent;
  margin-top: 24px;
}

.partners-container.is-embed .title {
  display: none;
}

.partners-container.is-embed .tabs {
  margin-left: 0;
  justify-content: flex-start;
}

.partners-container.is-embed .content {
  padding: 0;
}

.title {
      font-family: PingFang SC, "Microsoft YaHei", sans-serif;
      font-size: 40px;
      font-weight: 600;
      line-height: 40px;
      text-align: center;
      color: #b8860b;
      margin-bottom: 2rem;
  }

.tabs {
  display: flex;
  gap: 40px;
  margin-bottom: 25px;
  margin-left: calc(40px + max(0px, (100% - 1280px) / 2));
}

.tab {
    cursor: pointer;
    padding: 8px 16px;
    color: #8b7355;
    font-size: 22px;
    font-weight: 400;
    position: relative;
    border-radius: 4px;
    transition: color 0.3s ease, font-weight 0.3s ease;
  }
  
  .tab:hover {
    color: #d4af37;
  }
  
  .tab.active {
    color: #daa520;
    font-weight: 600;
  }

.content {
  padding: 0 40px;
  min-height: 320px;
}

/* 每行 6 张，共 3 行，居中 */
.partners-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  justify-items: center;
  align-items: center;
}

.partners-item {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 160px;
  min-height: 92px;
  padding: 10px 12px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid rgba(218, 165, 32, 0.3);
  border-radius: var(--radius-md);
  box-shadow: 0 2px 8px rgba(218, 165, 32, 0.15);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  overflow: hidden;
}

.partner-image {
  height: auto;
  width: auto;
  max-width: 100%;
  max-height: 64px;
  object-fit: contain;
}

.partners-item:hover {
  z-index: 3;
  transform: translateY(-4px) scale(1.08);
  box-shadow: 0 6px 20px rgba(218, 165, 32, 0.3);
  border-color: rgba(218, 165, 32, 0.5);
}


.empty-tip {
  font-size: 16px;
  color: rgba(139, 105, 20, 0.5);
  padding: 4rem 0;
}

@media (max-width: 1200px) {
  .partners-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem 1rem;
  }
}

@media (max-width: 1023px) {
.partners-container {
    padding: 4rem 0;
    overflow: hidden;
    background: linear-gradient(180deg, #fdfdfc 0%, #f8f8f6 100%);
    text-align: center;
  }

  .title {
    font-size: 28px;
    margin-bottom: 1.5rem;
  }

  .tabs {
    margin-bottom: 2rem;
    margin-left: 20px;
    padding: 0 20px;
  }

  .tab {
    padding: 10px 20px;
    font-size: 15px;
  }

  .content {
    padding: 0 20px;
  }

  .partners-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem 0.75rem;
  }

  .partners-item {
    max-width: 100px;
    padding: 8px;
  }

  .partner-image {
    max-height: 44px;
  }

}

@media (max-width: 480px) {
  .partners-container {
    padding: 1.5rem 4%;
  }

  .title {
    font-size: 24px;
    margin-bottom: 1rem;
  }

  .partners-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 0.5rem;
  }

  .partners-item {
    max-width: 80px;
    padding: 6px;
  }

  .partner-image {
    max-height: 36px;
  }
}
</style>
